import React, { useState } from "react";
import {
  Box,
  TextField,
  Button,
  Typography,
  Paper,
  CircularProgress,
  IconButton,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import LoginIcon from "@mui/icons-material/Login";
import TrainIcon from "@mui/icons-material/Train";
import { useNavigate } from "react-router-dom";
import apiClient from "../BaseUrl/apiClient";
import TokenService from "../service/TokenService";
import { useAuthentication } from "../customHook/useAuthentecation";
import { IP, Copyright } from "../constants/Constants";

const LoginPage = () => {
  const [buttonLoading, setButtonLoading] = useState(false);
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [showPassword, setShowPassword] = useState(false);
  const [pageLoader, setPageLoader] = useState(false);

  const { setAuth } = useAuthentication();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  /* ================= API (UNCHANGED) ================= */
  const loginUrl = `/login/login.php`;
  const verifyOtpUrl = `/login/verify-otp.php`;

  /* ================= LOGIN ================= */
  const handleLoginSubmit = async () => {
    setPageLoader(true);
    setButtonLoading(true);
    try {
      const response = await apiClient.post(loginUrl, {
        userId,
        password,
      });
      alert("HI " + response.data.message);
      setStep(2);
    } catch (error) {
      alert(error.response?.data?.message || "Login failed");
    }
    setPageLoader(false);
    setButtonLoading(false);
  };

  /* ================= OTP VERIFY ================= */
  const handleOtpSubmit = async () => {
    setPageLoader(true);
    setButtonLoading(true);
    try {
      const response = await apiClient.post(verifyOtpUrl, {
        userId,
        otp,
      });
      TokenService.setTokenDetails(response.data.data);
      setAuth(response.data.data);
      navigate("/dashboard");
    } catch (error) {
      alert(error.response?.data?.message);
    }
    setPageLoader(false);
    setButtonLoading(false);
  };

  /* ================= OTP UI ================= */
  const handleOtpChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;

    const arr = otp.split("");
    arr[index] = value;
    const newOtp = arr.join("");
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-${index + 1}`)?.focus();
    }

    if (newOtp.length === 6 && !newOtp.includes("")) {
      handleOtpSubmit();
    }
  };

  const handleOtpKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      e.preventDefault();
      const arr = otp.split("");
      if (arr[index]) {
        arr[index] = "";
        setOtp(arr.join(""));
      } else if (index > 0) {
        document.getElementById(`otp-${index - 1}`)?.focus();
        arr[index - 1] = "";
        setOtp(arr.join(""));
      }
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData
      .getData("text")
      .replace(/\D/g, "")
      .slice(0, 6);

    setOtp(pasted.padEnd(6, ""));
    if (pasted.length === 6) handleOtpSubmit();
  };

  return (
    <Box
      sx={{
        position: "fixed",
        inset: 0,
        display: "flex",
        backgroundColor: "#f7f9fc",
      }}
    >
      {/* PAGE LOADER */}
      {pageLoader && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            bgcolor: "rgba(255,255,255,0.6)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <CircularProgress />
        </Box>
      )}

      {/* LEFT SECTION */}
      {!isMobile && (
        <Box
          sx={{
            width: "40%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box textAlign="center">
            <TrainIcon sx={{ fontSize: 90, color: "#1976d2" }} />
            <Typography variant="h4" fontWeight={700} mt={2}>
              Coach Cleaning
            </Typography>
            <Typography color="text.secondary">
              Monitoring System
            </Typography>
          </Box>
        </Box>
      )}

      {/* RIGHT BLUE PANEL */}
      <Box
        sx={{
          width: { xs: "100%", md: "60%" },
          height: "100%",
          background:
            "linear-gradient(135deg,#0d6efd,#0b5ed7)",
          borderTopLeftRadius: { md: "140px" },
          borderBottomLeftRadius: { md: "140px" },
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          animation: "bgMove 18s ease infinite",
          backgroundSize: "200% 200%",
          "@keyframes bgMove": {
            "0%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
            "100%": { backgroundPosition: "0% 50%" },
          },
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: 360,
            p: 4,
            borderRadius: 3,
          }}
        >
          <Typography variant="h5" fontWeight={700} mb={1}>
            Hello!
          </Typography>
          <Typography color="text.secondary" mb={3}>
            Sign in to get started
          </Typography>

          {/* LOGIN */}
          {step === 1 && (
            <>
              <TextField
                fullWidth
                placeholder="User ID"
                value={userId}
                onChange={(e) => setUserId(e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                placeholder="Password"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                sx={{ mb: 3 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() =>
                          setShowPassword(!showPassword)
                        }
                      >
                        {showPassword ? (
                          <VisibilityOff />
                        ) : (
                          <Visibility />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                fullWidth
                variant="contained"
                startIcon={<LoginIcon />}
                onClick={handleLoginSubmit}
                disabled={buttonLoading}
                sx={{
                  py: 1.3,
                  borderRadius: 5,
                  fontWeight: 600,
                }}
              >
                LOGIN
              </Button>
            </>
          )}

          {/* OTP */}
          {step === 2 && (
            <>
              <Typography fontWeight={600} mb={2}>
                Enter OTP from Your E-Mail
              </Typography>

              <Box
                display="flex"
                gap={1}
                mb={3}
                onPaste={handleOtpPaste}
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <TextField
                    key={i}
                    id={`otp-${i}`}
                    value={otp[i] || ""}
                    onChange={(e) =>
                      handleOtpChange(e.target.value, i)
                    }
                    onKeyDown={(e) =>
                      handleOtpKeyDown(e, i)
                    }
                    inputProps={{
                      maxLength: 1,
                      inputMode: "numeric",
                      style: {
                        textAlign: "center",
                        fontSize: 22,
                        fontWeight: 700,
                      },
                    }}
                    sx={{ width: 46 }}
                  />
                ))}
              </Box>

              <Button
                fullWidth
                variant="contained"
                onClick={handleOtpSubmit}
                disabled={buttonLoading}
                sx={{
                  py: 1.3,
                  borderRadius: 5,
                  fontWeight: 600,
                }}
              >
                VERIFY OTP
              </Button>
            </>
          )}

          <Typography fontSize={11} mt={3} align="center">
            🔐 Protected by Two-Factor Authentication
          </Typography>
          <Typography fontSize={11} align="center">
            Your IP: {IP}
          </Typography>
          {Copyright()}
        </Paper>
      </Box>
    </Box>
  );
};

export default LoginPage;
